import { Animated, Platform, StyleSheet, Text, View } from 'react-native'
import React, { useRef, useEffect, useState, useCallback } from 'react'
import { ThemedView, ThemedText } from '@app/theme'
import ScreenWrapper from '@app/hoc/ScreenWrapper'
import { BottomTabParamList, PrivateStackParamList } from '@app/navigationApp/types'
import { NativeStackScreenProps } from '@react-navigation/native-stack'
import LottieView from 'lottie-react-native'
import { endpoints } from '@app/api/apis'
import axios from 'axios'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { useAppDispatch, useAppSelector } from '@app/store/hooks'
import { showErrorSheet } from '@app/store/slices/errorSlice'
import DSPLogo from '@app/assets/svg/DSPLogo'
import { addCommasToNumber, trackEvent } from '@app/utils'
import { LENDER_ID } from '@app/types/regix'
import TataLogo from '@app/assets/svg/TataLogo'
import { updateBFLCheck, updateisLMSUser } from '@app/store/slices/commonSlice'
import fetchUserDetails from '@app/helper/fetchUserInfo'
import useIsRedirectionJourney from '@app/hooks/useIsRedirectionJourney'
import ROUTE from '@app/navigationApp/routes'
import FullRedirectionJourney from './components/FullRedirectionJourney'

type ScreenProps = NativeStackScreenProps<PrivateStackParamList & BottomTabParamList, 'CREDIT_LOADING'>


export const checkCreditStatusApi = async (payload: {
    applicationId: string;
}) => {

    let applicationId = payload.applicationId;
    let applicationIdStored = await AsyncStorage.getItem('application_id')

    if (applicationIdStored) {
        applicationId = applicationIdStored;
    }

    const response = await axios.get(endpoints.creditPollingAPI + applicationId);

    return response;

}


const LoadingState = () => {

    return (
        <ThemedView flex={1} bg='bg-surface-primary' justifyContent='center' alignItems='center'>
            <LottieView
                source={require('../../assets/lottie/sand_timer.json')}
                style={{
                    width: 100,
                    height: 100,
                }}
                autoPlay={true}
                loop={true}
            />

            <ThemedText mt='md' variant='b3r' color='text-primary'>You're just a step away! Getting your account ready.</ThemedText>
            <ThemedText mt='sm' variant='b4r' color='text-secondary'>It may take 10-15 Seconds...</ThemedText>
        </ThemedView>
    )
}


const ApprovedState = ({ creditLimit, onSuccess , lenderAccountId }: { creditLimit: number, onSuccess: () => void , lenderAccountId: string }) => {

    const opacity = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        Animated.timing(opacity, {
            toValue: 1,
            duration: 500,
            useNativeDriver: true,
        }).start();
    }, []);
    return (
        <ThemedView padding='2xl' flex={1} bg='bg-surface-primary' justifyContent='center'>
            <LottieView
                source={require('../../assets/lottie/unlockNew.json')}
                style={{
                    width: 350,
                    height: 400,
                    position: 'absolute',
                    zIndex: 1000,
                    alignSelf: 'center',
                }}
                onAnimationFinish={() => {
                    onSuccess()
                }}
                autoPlay={true}
                loop={false}
            />
            <Animated.View style={{ opacity }}>
                <ThemedText textAlign='center' variant='h3s'>Congratulations!</ThemedText>
                <ThemedText mt='xs' textAlign='center' variant='b4r' color='text-secondary'>{`Your account has been\nsuccessfully opened`}</ThemedText>
                <ThemedView padding='lg' borderRadius='md' justifyContent='center' mt='xl' borderWidth={1} borderColor='border-secondary' style={styles.shadowStyle}>
                    <ThemedText textAlign='center' variant='b4r' color='text-primary'>Available cash</ThemedText>
                    <ThemedText textAlign='left' variant='h1s' color='text-primary'>
                    <ThemedText fontFamily='Inter' textAlign='left' variant='h1s' color='text-primary'>₹</ThemedText>
                        {addCommasToNumber(creditLimit)}
                    </ThemedText>
                    <ThemedText textAlign='left' mt='xl' variant='b5r' color='text-secondary'>100% of total limit available</ThemedText>
                    <ThemedView height={5} borderRadius='full' bg='border-success' mt='sm' />
                </ThemedView>
                <ThemedView flexDirection='row' alignItems='center' justifyContent='center' mt='lg'>
                    <ThemedText style={{ marginRight: lenderAccountId?.toLowerCase() === LENDER_ID.DSP ? -50 : 10 }} textAlign='center' variant='b4r' color='text-primary'>Lending Partner</ThemedText>
                    {lenderAccountId?.toLowerCase() === LENDER_ID.DSP ? <DSPLogo /> : <TataLogo />}
                </ThemedView>
            </Animated.View>
        </ThemedView>
    )
}

const ApprovedStateEnhanced = ({ creditLimit, onSuccess , lenderAccountId }: { creditLimit: number, onSuccess: () => void , lenderAccountId: string }) => {

    const opacity = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        Animated.timing(opacity, {
            toValue: 1,
            duration: 500,
            useNativeDriver: true,
        }).start();
    }, []);


    return (
        <ThemedView padding='2xl' flex={1} bg='bg-surface-primary' justifyContent='center'>
            <LottieView
                source={require('../../assets/lottie/unlockNew.json')}
                style={{
                    width: 350,
                    height: 400,
                    position: 'absolute',
                    zIndex: 1000,
                    alignSelf: 'center',
                }}
                onAnimationFinish={() => {
                    onSuccess()
                }}
                autoPlay={true}
                loop={false}
            />
            <Animated.View style={{ opacity }}>
                <ThemedText textAlign='center' variant='h3s'>Congratulations!</ThemedText>
                <ThemedText mt='xs' textAlign='center' variant='b4r' color='text-secondary'>{`Available limit increased successfully`}</ThemedText>
                <ThemedText mt='xl' textAlign='center' variant='h1s' color='text-success'>+ ₹{addCommasToNumber(creditLimit)}</ThemedText>
                <ThemedView flexDirection='row' alignItems='center' justifyContent='center' mt='lg'>
                    <ThemedText style={{ marginRight: lenderAccountId?.toLowerCase() === LENDER_ID.DSP ? -50 : 10 }} textAlign='center' variant='b4r' color='text-primary'>Lending Partner</ThemedText>
                    {lenderAccountId?.toLowerCase() === LENDER_ID.DSP ? <DSPLogo /> : <TataLogo />}
                </ThemedView>
            </Animated.View>
        </ThemedView>
    )
}

const CreditLoading = ({ navigation, route }: ScreenProps) => {

    const [creditStatus, setCreditStatus] = useState<'loading' | 'success' | 'error'>('loading')
    const applicationId = useAppSelector(state => state.auth?.userDetails?.linkedApplications[0]?.applicationId);
    const applicationType = useAppSelector(state => state.auth?.userDetails?.linkedApplications[0]?.applicationType);
    const lenderId = useAppSelector(state => state.auth?.userDetails?.linkedApplications[0]?.lenderAccountId);

    const {isFullRedirectionJourney} = useIsRedirectionJourney();
    const showHome = useAppSelector(state => state.platformParams.showHome);

    const {showDownloadAppScreen} = route.params || {};





    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const dispatch = useAppDispatch();
    const [creditLimit, setCreditLimit] = useState(0);
    const [isPrimaryApplication, setIsPrimaryApplication] = useState(false);



    // Polling API to check the credit status
    const checkCreditStatus = useCallback(async () => {
        try {
            const response = await checkCreditStatusApi({ applicationId: applicationId })

            if (response.data.creditApplicationApprovalStatus === 'APPROVED') {
                trackEvent('CREDIT_LOADING_APPROVED')
                if (intervalRef.current) {
                    clearInterval(intervalRef.current)
                }
                setIsPrimaryApplication(response?.data?.isPrimaryApplication)
                setCreditLimit(response?.data?.creditLimit)
                setCreditStatus('success')
                if(!isFullRedirectionJourney && !showDownloadAppScreen) {
                    setTimeout(() => {
                        onSuccess()
                    }, 2000)
                }               
            }
        } catch (err: any) {
            trackEvent('CREDIT_LOADING_ERROR')
            if (intervalRef.current) {
                clearInterval(intervalRef.current)
            }
            setCreditStatus('error')
            dispatch(showErrorSheet({
                isErrorSheetVisible: true,
                errorMessage: err?.data?.message,
                closeOnBackdropPress: true,
                errorType: err?.data?.voltErrorCode || err?.data?.statusCode,
                ctaLabel: 'Okay'
            }));

        }
    }, [applicationId])


    useEffect(() => {
        //check credit status then start polling
        checkCreditStatus()

        let isRunning = false;
        intervalRef.current = setInterval(() => {
            if (!isRunning) {
                isRunning = true;
                checkCreditStatus().finally(() => {
                    isRunning = false;
                });
            }
        }, 3000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []);





    const onSuccess = async () => {
        fetchUserDetails()
        // go to LMS
        dispatch(updateBFLCheck({isBFLLoan : false}))
        if (await AsyncStorage.getItem("application_id") !== null && !(applicationType === "CREDIT_AGAINST_SECURITIES_BORROWER")) {
            dispatch(updateisLMSUser({ isLMSUser: true }));
            navigation.reset({
                index:  1,
                routes: [
                    { name: showHome ? 'HOME' : 'DASHBOARD_SCREEN' },
                    { name: 'PLEDGE_DETAILS', params: { source: 'CREDIT_LOADING' } },
                ],
            });
        }
        else {
            dispatch(updateisLMSUser({ isLMSUser: true }));
            if (Platform.OS === 'web') {
                navigation.navigate(showHome ? 'HOME' : 'DASHBOARD_SCREEN')
            } else {
                navigation.reset({
                    index: 1,
                    routes: [
                        { name: 'HOME' },
                    ],
                });
            }
        }
    }

    return (
        <ScreenWrapper>

            {creditStatus === 'loading' && (
                <LoadingState />
            )}
            {creditStatus === 'success' && isPrimaryApplication && !isFullRedirectionJourney && !showDownloadAppScreen && (
                <ApprovedState onSuccess={onSuccess} creditLimit={creditLimit} lenderAccountId={lenderId} />
            )}
            {creditStatus === 'success' && !isPrimaryApplication && !isFullRedirectionJourney && !showDownloadAppScreen && (
                <ApprovedStateEnhanced onSuccess={onSuccess} creditLimit={creditLimit} lenderAccountId={lenderId} />
            )}
            {(isFullRedirectionJourney || showDownloadAppScreen) && creditStatus === 'success' && (
                <FullRedirectionJourney approvedCreditLimit={creditLimit} />
            )}
        </ScreenWrapper>
    )
}

export default CreditLoading

const styles = StyleSheet.create({
    shadowStyle: {
        shadowColor: "rgba(12, 25, 86, 0.08)",
        shadowOffset: {
            width: 0,
            height: 20,
        },
        shadowRadius: 40,
        elevation: 40,
        shadowOpacity: 1,
    }
})